import { useRef, useState, useEffect, useCallback } from 'react';
import { UploadCloud, FileText, RefreshCw, Trash2, Info, AlertTriangle } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/States';
import { IndexStatusBadge } from '../../components/StatusBadges';
import { Modal, ConfirmDialog } from '../../components/ui/Modal';
import { Field, Select } from '../../components/ui/Field';
import { cn, formatBytes, formatRelativeTime } from '../../lib/utils';
import { toast } from '../../store/uiStore';
import { useModelsStore } from '../../store/modelsStore';
import { agentFilesService } from '../../services/agentFilesService';
import { AGENT_STUDIO_PERMISSIONS, useAgentStudioPermissions } from '../../lib/permissions';
import { ApiError } from '../../services/apiClient';
import type { Agent, AgentFile } from '../../types';

export function FilesTab({ agent, patch }: { agent: Agent; patch: (p: Partial<Agent>) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [details, setDetails] = useState<AgentFile | null>(null);
  const [toDelete, setToDelete] = useState<AgentFile | null>(null);
  const [files, setFiles] = useState<AgentFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const embeddings = useModelsStore((s) => s.embeddings);
  const embeddingModel = agent.embeddingModel || 'text-embedding-3-small';
  const canDelete = useAgentStudioPermissions().has(AGENT_STUDIO_PERMISSIONS.agentsDelete);

  // Atualiza a lista local e espelha a contagem no agente (badge da aba "Arquivos").
  const applyFiles = useCallback((list: AgentFile[]) => {
    setFiles(list);
    patch({ files: list });
  }, [patch]);

  const refresh = useCallback(async () => {
    try {
      applyFiles(await agentFilesService.list(agent.id));
    } catch {
      /* silencioso */
    }
  }, [agent.id, applyFiles]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Polling enquanto houver arquivos em processamento.
  useEffect(() => {
    const processing = files.some((f) => f.indexStatus === 'pending' || f.indexStatus === 'indexing');
    if (!processing) return;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [files, refresh]);

  async function addFiles(fileList: FileList | null) {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      const updated = await agentFilesService.upload(agent.id, Array.from(fileList), embeddingModel);
      applyFiles(updated);
      toast.success(`${fileList.length} arquivo(s) enviado(s). Indexação iniciada.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha no upload.');
    } finally {
      setUploading(false);
    }
  }

  async function confirmRemove() {
    if (!toDelete || !canDelete) return;
    const fileId = toDelete.id;
    setToDelete(null);
    applyFiles(files.filter((f) => f.id !== fileId));
    try {
      await agentFilesService.remove(agent.id, fileId);
      toast.info('Arquivo removido.');
    } catch {
      toast.error('Falha ao remover.');
      refresh();
    }
  }

  async function reindex(fileId: string) {
    try {
      await agentFilesService.reindex(agent.id, fileId);
      toast.info('Reindexação iniciada.');
      refresh();
    } catch {
      toast.error('Falha ao reindexar.');
    }
  }

  // Arquivos indexados com um modelo de embedding diferente do selecionado atualmente.
  const mismatch = files.some((f) => f.embeddingModel && f.embeddingModel !== embeddingModel);

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <Field label="Modelo de embedding" hint="usado para indexar e buscar os arquivos (RAG)" className="mb-0">
          <Select value={embeddingModel} onChange={(e) => patch({ embeddingModel: e.target.value })}>
            {embeddings.length === 0 && <option value={embeddingModel}>{embeddingModel}</option>}
            {embeddings.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </Select>
        </Field>
        {mismatch && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
            <AlertTriangle className="h-3.5 w-3.5" />
            Há arquivos indexados com outro modelo de embedding. Reindexe-os para buscar com o modelo atual.
          </p>
        )}
      </Card>

      <Card
        className={cn(
          'cursor-pointer border-2 border-dashed p-10 text-center transition-colors',
          dragging ? 'border-brand-orange bg-brand-orange/5' : 'border-line',
          uploading && 'pointer-events-none opacity-60',
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-cloud">
          <UploadCloud className="h-7 w-7 text-brand-orange" />
        </div>
        <p className="text-sm font-medium text-ink">
          {uploading ? 'Enviando…' : 'Arraste arquivos aqui ou clique para selecionar'}
        </p>
        <p className="mt-1 text-xs text-slate">PDF, DOCX, CSV, TXT · indexação real (embeddings → Qdrant)</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.csv,.txt,.md,.json"
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </Card>

      {files.length === 0 ? (
        <EmptyState icon={FileText} title="Nenhum arquivo anexado" description="Os arquivos viram contexto recuperável (RAG) para o agente." />
      ) : (
        <Card>
          <div className="divide-y divide-line">
            {files.map((file) => (
              <div key={file.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cloud text-slate">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{file.name}</p>
                  <p className="text-xs text-slate">
                    {file.type.toUpperCase()} · {formatBytes(file.sizeBytes)} · {formatRelativeTime(file.uploadedAt)}
                    {file.indexStatus === 'ready' && file.chunkCount ? ` · ${file.chunkCount} chunks` : ''}
                  </p>
                </div>
                <IndexStatusBadge status={file.indexStatus} />
                <div className="flex items-center gap-1">
                  <button onClick={() => setDetails(file)} className="rounded-lg p-1.5 text-slate hover:bg-cloud hover:text-ink" aria-label="Metadados">
                    <Info className="h-4 w-4" />
                  </button>
                  <button onClick={() => reindex(file.id)} className="rounded-lg p-1.5 text-slate hover:bg-cloud hover:text-ink" aria-label="Reindexar">
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  {canDelete && (
                    <button onClick={() => setToDelete(file)} className="rounded-lg p-1.5 text-slate hover:bg-danger/10 hover:text-danger" aria-label="Remover">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {canDelete && (
      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmRemove}
        title="Excluir arquivo"
        message={`Excluir "${toDelete?.name}"? Os vetores indexados deste arquivo também serão removidos. Esta ação não pode ser desfeita.`}
      />
      )}

      <Modal open={!!details} onClose={() => setDetails(null)} title="Metadados do arquivo">
        {details && (
          <dl className="space-y-2 text-sm">
            <Row label="Nome" value={details.name} />
            <Row label="Tipo" value={details.type.toUpperCase()} />
            <Row label="Tamanho" value={formatBytes(details.sizeBytes)} />
            <Row label="Status de indexação" value={details.indexStatus} />
            <Row label="Chunks" value={String(details.chunkCount ?? 0)} />
            <Row label="Modelo de embedding" value={details.embeddingModel ?? '—'} />
            {details.error && <Row label="Erro" value={details.error} />}
            <Row label="Enviado em" value={new Date(details.uploadedAt).toLocaleString('pt-BR')} />
            <Row label="ID" value={details.id} />
          </dl>
        )}
      </Modal>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-1.5 last:border-0">
      <dt className="text-slate">{label}</dt>
      <dd className="font-medium text-ink">{value}</dd>
    </div>
  );
}
